/**
 * HeaderFilters Component
 *
 * Collapsible filter controls used by Header.
 * Class component for Chrome 68 compatibility.
 */

import React, { Component } from 'react';

class HeaderFilters extends Component {
    constructor(props) {
        super(props);
        this.handleCategoryDropdownChangeSingle = this.handleCategoryDropdownChangeSingle.bind(this);
        this.handleSourceChange = this.handleSourceChange.bind(this);
    }

    handleCategoryClick(category) {
        var self = this;
        return function() {
            if (self.props.onCategoryChange) {
                self.props.onCategoryChange(category);
            }
        };
    }

    handleSourceChange(event) {
        if (this.props.onSourceChange) {
            this.props.onSourceChange(event.target.value);
        }
    }

    handleSortClick(sortBy) {
        var self = this;
        return function() {
            if (self.props.onSortChange) {
                self.props.onSortChange(sortBy);
            }
        };
    }

    handleCategoryDropdownChangeSingle(event) {
        if (this.props.onCategoryChange) {
            var value = event.target.value;
            this.props.onCategoryChange(value === 'all' ? 'all' : value);
        }
        // Reset to first item so selecting the same category again will toggle off
        event.target.value = 'all';
    }

    render() {
        var props = this.props;
        var self = this;
        var selectedCategories = props.selectedCategories || [];
        var activeSorts = props.activeSorts || [];

        // Helper to check if category is selected
        var isCategorySelected = function(category) {
            return selectedCategories.indexOf(category) !== -1;
        };

        // Helper to get sort info + precedence index
        var getSortMeta = function(field) {
            for (var i = 0; i < activeSorts.length; i++) {
                if (activeSorts[i].field === field) {
                    return { sort: activeSorts[i], index: i };
                }
            }
            return { sort: null, index: -1 };
        };

        var showSortPrecedence = activeSorts.length > 1;

        // Sort chevron display
        // For Name: ▼ = A-Z (asc), ▲ = Z-A (desc)
        // For Rating/Size/Date: ▼ = highest/biggest/newest (desc), ▲ = lowest/smallest/oldest (asc)
        var getSortChevron = function(field) {
            var meta = getSortMeta(field);
            var sortInfo = meta && meta.sort ? meta.sort : null;
            if (!sortInfo) return '';
            if (field === 'name') {
                return sortInfo.direction === 'asc' ? '▼' : '▲';
            }
            return sortInfo.direction === 'desc' ? '▼' : '▲';
        };

        var getSortTitle = function(field) {
            var meta = getSortMeta(field);
            if (!meta || !meta.sort) return '';
            var s = meta.sort;
            var dir = s.direction || '';
            if (field === 'name') {
                return dir === 'asc' ? 'Name (A→Z)' : 'Name (Z→A)';
            }
            if (field === 'rating') {
                return dir === 'desc' ? 'Rating (high→low)' : 'Rating (low→high)';
            }
            if (field === 'size') {
                return dir === 'desc' ? 'Size (high→low)' : 'Size (low→high)';
            }
            if (field === 'updated') {
                return dir === 'desc' ? 'Date (recent→oldest)' : 'Date (oldest→recent)';
            }
            return '';
        };

        var renderSortButton = function(field, label) {
            var meta = getSortMeta(field);
            var active = !!(meta && meta.sort);
            var chevron = active ? getSortChevron(field) : '';
            var cls = 'bb-filter-btn bb-filter-btn-sm bb-sort-chip' + (active ? ' active' : '');
            var kids = [React.createElement('span', { key: 'label', className: 'bb-sort-chip-label' }, label)];
            if (active && showSortPrecedence) {
                kids.push(
                    React.createElement('span', {
                        key: 'prec',
                        className: 'bb-sort-chip-dot bb-sort-chip-dot-left',
                        'aria-hidden': 'true'
                    }, String(meta.index + 1))
                );
            }
            if (active) {
                kids.push(
                    React.createElement('span', {
                        key: 'dir',
                        className: 'bb-sort-chip-dot bb-sort-chip-dot-right',
                        'aria-hidden': 'true'
                    }, chevron)
                );
            }
            return React.createElement('button', {
                key: field,
                className: cls,
                onClick: self.handleSortClick(field),
                title: getSortTitle(field)
            }, kids);
        };

        // Build category buttons - show ALL categories
        var categoryButtons = [
            React.createElement('button', {
                key: 'all',
                className: 'bb-filter-btn' + (selectedCategories.length === 0 ? ' active' : ''),
                onClick: this.handleCategoryClick('all')
            }, 'All')
        ];

        // Add ALL categories as buttons (not just first 8)
        var categories = props.categories || [];
        categories.forEach(function(category) {
            categoryButtons.push(
                React.createElement('button', {
                    key: category,
                    className: 'bb-filter-btn' + (isCategorySelected(category) ? ' active' : ''),
                    onClick: self.handleCategoryClick(category)
                }, category)
            );
        });

        // Category dropdown options (for extra small screens)
        var categoryOptions = [
            React.createElement('option', { key: 'all', value: 'all' }, 'All Categories')
        ];
        categories.forEach(function(category) {
            var isSelected = isCategorySelected(category);
            categoryOptions.push(
                React.createElement('option', { key: category, value: category },
                    (isSelected ? '✓ ' : '') + category)
            );
        });

        // Selected categories chips for XS
        var selectedChips = [];
        selectedCategories.forEach(function(cat) {
            selectedChips.push(
                React.createElement('span', { key: cat, className: 'bb-selected-cat-chip' }, cat)
            );
        });

        // Source options with proper labels
        var sourceLabels = {
            fdroid: 'F-Droid',
            uptodown: 'Uptodown',
            apkmirror: 'APKMirror',
            apkpure: 'APKPure'
        };
        var sourceOptions = [
            React.createElement('option', { key: 'all', value: 'all' }, 'All Sources')
        ];
        var sources = props.sources || [];
        sources.forEach(function(source) {
            var label = sourceLabels[source] || source.charAt(0).toUpperCase() + source.slice(1);
            sourceOptions.push(
                React.createElement('option', { key: source, value: source }, label)
            );
        });

        return React.createElement('div', { className: 'bb-header-filters' },
            // Categories - chips for sm+, dropdown for xs
            React.createElement('div', { className: 'bb-filter-section' },
                React.createElement('div', { className: 'bb-filter-label' }, 'Category:'),
                // Chip buttons - hidden on extra small screens
                React.createElement('div', { className: 'bb-filter-buttons d-none d-sm-flex' }, categoryButtons),
                // Dropdown - shown only on extra small screens
                React.createElement('select', {
                    className: 'bb-select bb-select-full d-sm-none',
                    value: 'all',
                    onChange: this.handleCategoryDropdownChangeSingle
                }, categoryOptions),
                selectedChips.length > 0 && React.createElement('div', { className: 'bb-selected-cats-xs d-sm-none' }, selectedChips)
            ),

            // Sort and Source row - chips for sm+, dropdowns for xs
            // For sm+: row with buttons
            React.createElement('div', { className: 'bb-filter-row bb-filter-row-spread d-none d-sm-flex' },
                // Sort buttons on left
                React.createElement('div', { className: 'bb-filter-group' },
                    React.createElement('span', { className: 'bb-filter-label' }, 'Sort:'),
                    React.createElement('div', { className: 'bb-sort-buttons' },
                        renderSortButton('name', 'Name'),
                        renderSortButton('rating', 'Rating'),
                        renderSortButton('size', 'Size'),
                        renderSortButton('updated', 'Date')
                    )
                ),

                // Source selector on right
                React.createElement('div', { className: 'bb-filter-group' },
                    React.createElement('label', { className: 'bb-filter-label' }, 'Source:'),
                    React.createElement('select', {
                        className: 'bb-select',
                        value: props.selectedSource || 'all',
                        onChange: this.handleSourceChange
                    }, sourceOptions)
                )
            ),

            // For xs: stacked dropdowns
            React.createElement('div', { className: 'bb-filter-dropdowns d-sm-none' },
                React.createElement('div', { className: 'bb-filter-dropdown-row' },
                    React.createElement('label', { className: 'bb-filter-label' }, 'Source:'),
                    React.createElement('select', {
                        className: 'bb-select bb-select-full',
                        value: props.selectedSource || 'all',
                        onChange: this.handleSourceChange
                    }, sourceOptions)
                ),
                React.createElement('div', { className: 'bb-filter-dropdown-row' },
                    React.createElement('label', { className: 'bb-filter-label' }, 'Sort:'),
                    React.createElement('div', { className: 'bb-sort-buttons bb-sort-buttons-xs' },
                        renderSortButton('name', 'Name'),
                        renderSortButton('rating', 'Rating'),
                        renderSortButton('size', 'Size'),
                        renderSortButton('updated', 'Date')
                    )
                )
            )
        );
    }
}

export default HeaderFilters;

